import { useEffect } from 'react';

const SITE_URL = 'https://global79lojistik.com';
const DEFAULT_OG_IMAGE = '/assets/images/logo.png';
const DEFAULT_LOCALE = 'tr_TR';

type SeoProps = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

function toAbsoluteUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

function setMetaTag(
  selector: string,
  createAttrs: Record<string, string>,
  content: string,
): void {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);
  if (!meta) {
    meta = document.createElement('meta');
    Object.entries(createAttrs).forEach(([key, value]) => meta.setAttribute(key, value));
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

export default function Seo({
  title,
  description,
  path,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(path);
    const imageUrl = toAbsoluteUrl(ogImage);

    document.documentElement.lang = 'tr';
    document.title = title;

    setMetaTag('meta[name="description"]', { name: 'description' }, description);
    if (keywords) {
      setMetaTag('meta[name="keywords"]', { name: 'keywords' }, keywords);
    }

    setMetaTag(
      'meta[name="robots"]',
      { name: 'robots' },
      noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    );

    setMetaTag('meta[property="og:type"]', { property: 'og:type' }, 'website');
    setMetaTag('meta[property="og:site_name"]', { property: 'og:site_name' }, 'Global79 Lojistik');
    setMetaTag('meta[property="og:locale"]', { property: 'og:locale' }, DEFAULT_LOCALE);
    setMetaTag('meta[property="og:title"]', { property: 'og:title' }, title);
    setMetaTag('meta[property="og:description"]', { property: 'og:description' }, description);
    setMetaTag('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    setMetaTag('meta[property="og:image"]', { property: 'og:image' }, imageUrl);

    setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let script = document.getElementById('seo-json-ld') as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'seo-json-ld';
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [description, jsonLd, keywords, noindex, ogImage, path, title]);

  return null;
}
